import useAuth from '@app/auth/use-auth';
import { DataTableActionBar } from '@app/components/data-table/data-table-action-bar';
import { SimpleDialog } from '@app/hooks/use-simple-dialog';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import { Separator } from '@shared/components/ui/separator';
import type { Table } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { BulkAction } from './types';

interface BulkActionBarProps<TData> {
  table: Table<TData>;
  actions: BulkAction[];
}

export function BulkActionBar<TData>({
  table,
  actions,
}: BulkActionBarProps<TData>) {
  const { user } = useAuth();
  const [openAction, setOpenAction] = useState<BulkAction | null>(null);

  const selectedRowIds = useMemo(
    () =>
      Object.entries(table.getState().rowSelection)
        .filter(([, isSelected]) => isSelected)
        .map(([id]) => id),
    [table.getState().rowSelection]
  );
  const selectedCount = selectedRowIds.length;

  // The uuids passed to the open action's modal — usually the selected row
  // ids, unless the action resolves a different entity (e.g. the underlying
  // PurchasedGood) from the rows.
  const selectedUuids = openAction?.resolveUuids
    ? openAction.resolveUuids(table)
    : selectedRowIds;
  const selectedFoodActivityUuids =
    openAction?.resolveFoodActivityUuids?.(table);

  const handleClose = () => setOpenAction(null);
  const handleCompleted = () => {
    setOpenAction(null);
    table.toggleAllRowsSelected(false);
  };

  const ActiveModalBody = openAction?.ModalBody;

  // Bulk actions are staff-only for now. Hooks above run unconditionally so the
  // early return is safe; the matching selection checkboxes are gated at each
  // table so non-staff users never see an actionable selection.
  if (!user?.isStaff) return null;

  return (
    <>
      <DataTableActionBar table={table} visible={selectedCount > 0}>
        <div className="flex h-7 items-center rounded-md border bg-gray-00 pl-2.5 pr-1">
          <Text
            variant="subtle"
            className="whitespace-nowrap text-xs text-gray-800"
          >
            {selectedCount} selected
          </Text>
          <Separator orientation="vertical" className="ml-2 mr-1 h-4" />
          <Button
            variant="ghost"
            size="icon"
            className="size-5"
            onClick={() => table.toggleAllRowsSelected(false)}
            aria-label="Clear selection"
            icon={<Icons.x className="size-3.5" />}
          />
        </div>

        <Separator orientation="vertical" className="h-5" />

        {actions.map((action) => {
          const Icon = Icons[action.icon];
          return (
            <Button
              key={action.kind}
              variant={action.variant ?? 'outline'}
              size="sm"
              icon={<Icon />}
              onClick={() => setOpenAction(action)}
            >
              {action.label}
            </Button>
          );
        })}
      </DataTableActionBar>

      <SimpleDialog
        open={!!openAction}
        setOpen={(next) => {
          if (!next) handleClose();
        }}
        header={openAction?.modalTitle ?? openAction?.label ?? ''}
        contentClassName={openAction?.modalContentClassName}
        content={
          ActiveModalBody ? (
            <ActiveModalBody
              selectedUuids={selectedUuids}
              selectedFoodActivityUuids={selectedFoodActivityUuids}
              selectedCount={selectedCount}
              onClose={handleClose}
              onCompleted={handleCompleted}
            />
          ) : (
            <></>
          )
        }
      />
    </>
  );
}
